/**
 * @file MediaPreviewModal — generic preview modal for PDFs and images.
 *
 * Reusable across domains (product, panel, integrations, …). Receives a
 * `resource` descriptor and renders the appropriate viewer inside a Modal:
 *   - PDF  → lazy-loaded <PdfViewer> (react-pdf + pdf.js worker)
 *   - image → <img> with object-fit:contain
 *
 * Locale-agnostic: all user-facing strings (close, download) are passed in
 * by the caller, keeping this component free of i18n dependencies.
 */

import { Suspense, lazy } from 'react'

import { getFileType } from '../../utils/getFileType.js'

import { Button } from '../Button/index.js'
import { Download } from '../icons/index.js'
import { Loading } from '../Loading.jsx'
import { Modal } from '../Modal/index.js'

import styles from './MediaPreviewModal.module.scss'

// PdfViewer pulls in react-pdf + the pdf.js worker (~430 kB). Lazy-load it so
// the chunk is fetched only when the user actually opens a PDF preview.
const PdfViewer = lazy(() => import('../PdfViewer/index.js'))

/**
 * @typedef {object} PreviewResource
 * @property {string}  label      - Human-readable name shown in the header
 * @property {string}  url        - Source URL (used for type detection and image rendering)
 * @property {string} [blobUrl]   - Validated blob: URL for PDFs (required when type==='pdf')
 */

/**
 * @param {object} props
 * @param {PreviewResource|null} props.resource     - Resource to preview; modal is closed when null
 * @param {() => void}           props.onClose
 * @param {(resource: PreviewResource) => void} [props.onDownload] - Footer download button; hidden when omitted
 * @param {string}               props.closeLabel    - aria-label for close button
 * @param {string}              [props.downloadLabel] - Text label on the download button (required if onDownload set)
 */
export function MediaPreviewModal({
  resource,
  onClose,
  onDownload,
  closeLabel,
  downloadLabel,
}) {
  if (!resource) return null

  const fileType = getFileType(resource.url)

  return (
    <Modal
      isOpen={!!resource}
      onClose={onClose}
      ariaLabel={resource.label}
      dialogClassName={styles.dialog}
    >
      <Modal.Header onClose={onClose} closeLabel={closeLabel}>
        <span className={styles.heading}>{resource.label}</span>
      </Modal.Header>

      <Modal.Content className={styles.content}>
        {fileType === 'pdf' && (
          resource.blobUrl || //built_ins
          resource.url        //additional resources from cms
        ) && (
          <Suspense fallback={<Loading />}>
            <PdfViewer file={resource.blobUrl || resource.url} ariaLabel={resource.label} />
          </Suspense>
        )}
        {fileType === 'image' && (
          <img
            src={resource.url}
            alt={resource.label}
            className={styles.image}
          />
        )}
      </Modal.Content>

      {onDownload && (
        <Modal.Footer>
          <Button
            type="button"
            kind="text"
            size="large"
            onClick={() => onDownload(resource)}
          >
            <Download width="0.875em" height="0.875em" />
            {downloadLabel}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  )
}

export default MediaPreviewModal
